import React from "react";

import PaymentCell from "./PaymentCell";
import { paymentFilterOptions } from "./paymentFilterOptions";
import DataTableColumnHeader from "../../../dataTable/DataTableColumnHeader";
import DataTableLinkCell from "../../../dataTable/DataTableLinkCell";
import { getOfferEditHref } from "../../../offers/tableParams/offerColumns";
import { userEditPath } from "../../../routes/routes";

import type { TFunction } from "i18next";

import type {
  TransactionOfferRow,
  TransactionPaymentRow,
  TransactionUserRow,
} from "./transactionColumns.types";
import type {
  ColumnLayout,
  DataTableColumnDef,
} from "../../../dataTable/dataTable.types";

export function getTransactionUserHref(
  row: Pick<TransactionUserRow, "user_uuid">,
  projectSlug: string | undefined,
  userAbility: boolean,
): string | undefined {
  return userAbility ? userEditPath(projectSlug, row.user_uuid) : undefined;
}

export function getTransactionOfferHref(
  row: Pick<TransactionOfferRow, "offer_uuid">,
  projectSlug: string | undefined,
  offerAbility: boolean,
): string | undefined {
  return offerAbility
    ? getOfferEditHref(projectSlug, row.offer_uuid)
    : undefined;
}

export function transactionEmailColumn<TData extends TransactionUserRow>(
  t: TFunction,
  projectSlug: string | undefined,
  userAbility: boolean,
  layout?: ColumnLayout,
): DataTableColumnDef<TData> {
  return {
    accessorKey: "email",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t("email")} />
    ),
    meta: { ...layout, searchable: true, label: t("email") },
    cell: ({ row }) => (
      <DataTableLinkCell
        href={getTransactionUserHref(row.original, projectSlug, userAbility)}
      >
        {row.original.email}
      </DataTableLinkCell>
    ),
  };
}

export function transactionNameColumn<TData extends TransactionUserRow>(
  t: TFunction,
  layout?: ColumnLayout,
): DataTableColumnDef<TData> {
  return {
    accessorKey: "name",
    enableHiding: true,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t("name")} />
    ),
    meta: { ...layout, searchable: true, label: t("name") },
    cell: ({ row }) => row.original.name,
  };
}

export function transactionPaymentColumn<TData extends TransactionPaymentRow>(
  t: TFunction,
  hasPaypalProvider: boolean,
): DataTableColumnDef<TData> {
  return {
    accessorKey: "payment",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t("payment")} />
    ),
    enableSorting: false,
    meta: {
      label: t("payment"),
      width: "8%",
      minWidth: 95,
      align: "center",
      filterOptions: paymentFilterOptions(t, hasPaypalProvider),
    },
    cell: ({ row }) => (
      <PaymentCell
        payment={row.original.payment}
        retailerName={row.original.retailer_name}
      />
    ),
  };
}

export function transactionOfferColumn<TData extends TransactionOfferRow>(
  header: string,
  projectSlug: string | undefined,
  offerAbility: boolean,
  layout?: ColumnLayout,
): DataTableColumnDef<TData> {
  return {
    accessorKey: "offer_label",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={header} />
    ),
    meta: { ...layout, searchable: true, label: header },
    cell: ({ row }) => (
      <DataTableLinkCell
        href={getTransactionOfferHref(row.original, projectSlug, offerAbility)}
      >
        {row.original.offer_label}
      </DataTableLinkCell>
    ),
  };
}
